<template>
  <div class="flex flex-col gap-32">
    <div class="bg-surface-default-low">
      <BuybackBreadcrumb class="mb-32" productName="product short name" />
      <SeoHero :price productName="product short name" />
    </div>
    <BuybackReassurance />
    <BuybackSearch @item-selected="handleItemSelected" />
    <BuybackCategories @category-click="handleCategoryClick" />
    <div class="bg-surface-default-low">
      <SeoFooterLinksContainer
        :sections="mockSeoSections"
        @link-click="handleLinkClick"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { navigateTo } from '#imports'

import type { Price } from '@backmarket/http-api'
import type { BuybackProduct } from '@backmarket/nuxt-layer-buyback/types'
import { toNuxtLink } from '@backmarket/nuxt-module-navigation/toNuxtLink'
import { removeEmptyValuesInObject } from '@backmarket/utils/object/removeEmptyValuesInObject'

import BuybackBreadcrumb from '~/scopes/buyback/components/BuybackBreadcrumb/BuybackBreadcrumb.vue'
import BuybackCategories from '~/scopes/buyback/components/BuybackCategories/BuybackCategories.vue'
import BuybackReassurance from '~/scopes/buyback/components/BuybackReassurance/BuybackReassurance.vue'
import BuybackSearch from '~/scopes/buyback/components/BuybackSearch/BuybackSearch.vue'
import type { SeoSection } from '~/scopes/buyback/components/SeoFooterLinks/SeoFooterLinks.types'
import SeoFooterLinksContainer from '~/scopes/buyback/components/SeoFooterLinks/SeoFooterLinksContainer.vue'
import SeoHero from '~/scopes/buyback/components/SeoHero/SeoHero.vue'
import { ROUTE_NAMES } from '~/scopes/buyback/constants'

const price: Price = {
  amount: '149.99',
  currency: 'EUR',
}

// Mock data for SEO footer links - multiple sections with pagination test
const mockSeoSections: SeoSection[] = [
  {
    title: 'Apple Devices',
    links: [
      { label: 'iPhone 15 Pro Max', url: '/buyback/iphone-15-pro-max' },
      { label: 'iPhone 15 Pro', url: '/buyback/iphone-15-pro' },
      { label: 'iPhone 15 Plus', url: '/buyback/iphone-15-plus' },
      { label: 'iPhone 15', url: '/buyback/iphone-15' },
      { label: 'iPhone 14 Pro Max', url: '/buyback/iphone-14-pro-max' },
      { label: 'iPhone 14 Pro', url: '/buyback/iphone-14-pro' },
      { label: 'iPhone 14 Plus', url: '/buyback/iphone-14-plus' },
      { label: 'iPhone 14', url: '/buyback/iphone-14' },
      { label: 'iPhone 13 Pro Max', url: '/buyback/iphone-13-pro-max' },
      { label: 'iPhone 13 Pro', url: '/buyback/iphone-13-pro' },
      { label: 'iPhone 13 Mini', url: '/buyback/iphone-13-mini' },
      { label: 'iPhone 13', url: '/buyback/iphone-13' },
      { label: 'iPhone 12 Pro Max', url: '/buyback/iphone-12-pro-max' },
      { label: 'iPhone 12 Pro', url: '/buyback/iphone-12-pro' },
      { label: 'iPhone 12 Mini', url: '/buyback/iphone-12-mini' },
      { label: 'iPhone 12', url: '/buyback/iphone-12' },
      { label: 'iPhone 11 Pro Max', url: '/buyback/iphone-11-pro-max' },
      { label: 'iPhone 11 Pro', url: '/buyback/iphone-11-pro' },
      { label: 'iPhone 11', url: '/buyback/iphone-11' },
      { label: 'iPhone XS Max', url: '/buyback/iphone-xs-max' },
      { label: 'iPhone XS', url: '/buyback/iphone-xs' },
      { label: 'iPhone XR', url: '/buyback/iphone-xr' },
      { label: 'iPhone X', url: '/buyback/iphone-x' },
      { label: 'iPhone SE (3rd gen)', url: '/buyback/iphone-se-3' },
      { label: 'iPhone SE (2nd gen)', url: '/buyback/iphone-se-2' },
      { label: 'iPad Pro 12.9" (6th gen)', url: '/buyback/ipad-pro-12-6' },
      { label: 'iPad Pro 12.9" (5th gen)', url: '/buyback/ipad-pro-12-5' },
      { label: 'iPad Pro 11" (4th gen)', url: '/buyback/ipad-pro-11-4' },
      { label: 'iPad Pro 11" (3rd gen)', url: '/buyback/ipad-pro-11-3' },
      { label: 'iPad Air (5th gen)', url: '/buyback/ipad-air-5' },
      { label: 'iPad Air (4th gen)', url: '/buyback/ipad-air-4' },
      { label: 'iPad (10th gen)', url: '/buyback/ipad-10' },
      { label: 'iPad (9th gen)', url: '/buyback/ipad-9' },
      { label: 'iPad Mini (6th gen)', url: '/buyback/ipad-mini-6' },
      { label: 'MacBook Pro 16" M3', url: '/buyback/macbook-pro-16-m3' },
      { label: 'MacBook Pro 14" M3', url: '/buyback/macbook-pro-14-m3' },
      { label: 'MacBook Air 15" M2', url: '/buyback/macbook-air-15-m2' },
      { label: 'MacBook Air 13" M2', url: '/buyback/macbook-air-13-m2' },
      { label: 'MacBook Air 13" M1', url: '/buyback/macbook-air-13-m1' },
      // Adding more to test pagination (40+ links)
      { label: 'Apple Watch Series 9', url: '/buyback/watch-9' },
      { label: 'Apple Watch Series 8', url: '/buyback/watch-8' },
      { label: 'Apple Watch Ultra 2', url: '/buyback/watch-ultra-2' },
      { label: 'Apple Watch SE', url: '/buyback/watch-se' },
      { label: 'AirPods Pro (2nd gen)', url: '/buyback/airpods-pro-2' },
      { label: 'AirPods Max', url: '/buyback/airpods-max' },
    ],
  },
  {
    title: 'Samsung Devices',
    links: [
      { label: 'Galaxy S24 Ultra', url: '/buyback/galaxy-s24-ultra' },
      { label: 'Galaxy S24+', url: '/buyback/galaxy-s24-plus' },
      { label: 'Galaxy S24', url: '/buyback/galaxy-s24' },
      { label: 'Galaxy S23 Ultra', url: '/buyback/galaxy-s23-ultra' },
      { label: 'Galaxy S23+', url: '/buyback/galaxy-s23-plus' },
      { label: 'Galaxy S23', url: '/buyback/galaxy-s23' },
      { label: 'Galaxy S22 Ultra', url: '/buyback/galaxy-s22-ultra' },
      { label: 'Galaxy S22+', url: '/buyback/galaxy-s22-plus' },
      { label: 'Galaxy S22', url: '/buyback/galaxy-s22' },
      { label: 'Galaxy Z Fold 5', url: '/buyback/galaxy-z-fold-5' },
      { label: 'Galaxy Z Flip 5', url: '/buyback/galaxy-z-flip-5' },
      { label: 'Galaxy Z Fold 4', url: '/buyback/galaxy-z-fold-4' },
      { label: 'Galaxy Z Flip 4', url: '/buyback/galaxy-z-flip-4' },
      { label: 'Galaxy Tab S9 Ultra', url: '/buyback/galaxy-tab-s9-ultra' },
      { label: 'Galaxy Tab S9+', url: '/buyback/galaxy-tab-s9-plus' },
      { label: 'Galaxy Tab S9', url: '/buyback/galaxy-tab-s9' },
    ],
  },
  {
    title: 'Google Devices',
    links: [
      { label: 'Pixel 8 Pro', url: '/buyback/pixel-8-pro' },
      { label: 'Pixel 8', url: '/buyback/pixel-8' },
      { label: 'Pixel 7 Pro', url: '/buyback/pixel-7-pro' },
      { label: 'Pixel 7', url: '/buyback/pixel-7' },
      { label: 'Pixel 7a', url: '/buyback/pixel-7a' },
      { label: 'Pixel 6 Pro', url: '/buyback/pixel-6-pro' },
      { label: 'Pixel 6', url: '/buyback/pixel-6' },
      { label: 'Pixel 6a', url: '/buyback/pixel-6a' },
      { label: 'Pixel Fold', url: '/buyback/pixel-fold' },
    ],
  },
]

async function handleItemSelected(item: BuybackProduct) {
  const query = {
    brand: item.brand,
    model: item.model,
    model_family: item.productLine,
    nextStep: 'false',
  }

  const linkParams = {
    name: ROUTE_NAMES.DEVICE_ASSESSMENT,
    type: 'internal',
    params: { slug: item.category, id: item.category },
    query: removeEmptyValuesInObject(query),
  }

  const link = toNuxtLink(linkParams)

  await navigateTo(link)
}

async function handleCategoryClick(categoryName: string) {
  switch (categoryName) {
    case 'iphone':
      await navigateTo({
        name: ROUTE_NAMES.DEVICE_ASSESSMENT,
        params: { slug: 'smartphone', id: 'smartphone' },
        query: { nextStep: 'false', brand: 'Apple' },
      })
      break
    default:
      await navigateTo({
        name: ROUTE_NAMES.DEVICE_ASSESSMENT,
        params: { slug: categoryName, id: categoryName },
        query: { nextStep: 'false' },
      })
      break
  }
}

async function handleLinkClick(_label: string) {
  // console.log('link clicked:', _label)
  // todo add tracking on link click
}
</script>
